import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';
import { useToast } from '@/hooks/use-toast';
import { createClient } from '@supabase/supabase-js';

// @ts-ignore
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://wfwjzimxwwikgfbxxnxt.supabase.co';
// @ts-ignore
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Indmd2p6aW14d3dpa2dmYnh4bnh0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2NjY5OTAsImV4cCI6MjA2ODI0Mjk5MH0.hWfvX7gV7bU1sWrJAiFHyBkXehws7RZf3c2cQVH5Ttc';

if (!supabaseUrl || !supabaseKey) {
    console.error('Supabase URL or Key is missing. Please check your environment variables.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

const ResetPasswordPage: React.FC = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [sessionError, setSessionError] = useState('');
    const navigate = useNavigate();
    const { toast } = useToast();

    // Extract and validate auth session from URL hash (Supabase recovery token)
    useEffect(() => {
        let mounted = true;
        let authSubscription: any = null;

        const verifySession = async () => {
            try {
                console.log('Starting session verification...');
                console.log('Current URL:', window.location.href);
                console.log('Hash:', window.location.hash);

                // Set up auth state listener FIRST
                const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                    console.log('Auth state changed:', event, session?.user?.id);
                    
                    if (event === 'PASSWORD_RECOVERY' && mounted) {
                        console.log('✅ Password recovery event detected');
                        setSessionError('');
                        setIsVerifying(false);
                    }
                    
                    if (event === 'SIGNED_IN' && session && mounted) {
                        console.log('✅ User signed in with recovery session');
                        setSessionError('');
                        setIsVerifying(false);
                    }
                });

                authSubscription = subscription;

                // Small delay to let Supabase process the hash
                await new Promise(resolve => setTimeout(resolve, 500));

                // Check current session
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                
                if (sessionError) {
                    console.error('Session error:', sessionError);
                }

                if (session && mounted) {
                    console.log('✅ Valid session found');
                    setSessionError('');
                    setIsVerifying(false);
                    return;
                }

                // If no session, try to extract and set from hash manually
                const hash = window.location.hash;
                console.log('Checking hash for tokens...');

                if (!hash || hash.length < 10) {
                    console.warn('No hash found in URL');
                    if (mounted) {
                        setSessionError('No recovery token found in URL. Please use the link from your password reset email.');
                        setIsVerifying(false);
                    }
                    return;
                }

                // Parse hash parameters
                const params = new URLSearchParams(hash.substring(1));
                const access_token = params.get('access_token');
                const refresh_token = params.get('refresh_token');
                const type = params.get('type');

                console.log('Hash params:', { 
                    hasAccessToken: !!access_token, 
                    hasRefreshToken: !!refresh_token, 
                    type 
                });

                if (!access_token) {
                    console.warn('No access token in hash');
                    if (mounted) {
                        setSessionError('Invalid recovery link. Please request a new password reset.');
                        setIsVerifying(false);
                    }
                    return;
                }

                // Manually set the session
                console.log('Attempting to set session manually...');
                const { data, error: setError } = await supabase.auth.setSession({
                    access_token,
                    refresh_token: refresh_token || ''
                });

                if (setError) {
                    console.error('Failed to set session:', setError);
                    if (mounted) {
                        if (setError.message.includes('expired')) {
                            setSessionError('Recovery link has expired. Please request a new password reset.');
                        } else {
                            setSessionError('Invalid recovery link. Please request a new password reset.');
                        }
                        setIsVerifying(false);
                    }
                    return;
                }

                if (data.session && mounted) {
                    console.log('✅ Session set successfully');
                    setSessionError('');
                    setIsVerifying(false);
                } else {
                    console.warn('Session set but no session returned');
                    if (mounted) {
                        setSessionError('Unable to verify recovery link. Please try again.');
                        setIsVerifying(false);
                    }
                }

            } catch (error: any) {
                console.error('Session verification error:', error);
                if (mounted) {
                    setSessionError('An error occurred. Please try again or request a new reset link.');
                    setIsVerifying(false);
                }
            }
        };

        verifySession();

        // Cleanup
        return () => {
            mounted = false;
            if (authSubscription) {
                authSubscription.unsubscribe();
            }
        };
    }, []);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast({
                title: "Passwords do not match",
                description: "Please ensure both passwords are the same.",
                variant: "destructive",
            });
            return;
        }

        if (password.length < 8) {
            toast({
                title: "Password too weak",
                description: "Password must be at least 8 characters.",
                variant: "destructive",
            });
            return;
        }

        setIsLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) {
                // Check if it's an invalid/expired token error
                if (error.message.includes('invalid') || error.message.includes('expired') || error.message.includes('session')) {
                    setSessionError('Recovery token has expired. Please request a new password reset link.');
                } else {
                    throw error;
                }
            } else {
                setIsSuccess(true);
                toast({
                    title: "Password updated",
                    description: "Your password has been successfully reset.",
                });
            }
        } catch (error: any) {
            toast({
                title: "Reset failed",
                description: error.message || "Failed to reset password. Please try again or request a new link.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-white overflow-hidden">
            <UniversalHeader title="CareVault" />

            <div className="flex-1 container mx-auto px-4 py-8 lg:py-12 flex items-center justify-center mt-20">
                <div className="w-full flex flex-col lg:flex-row bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
                    {/* Left Side - Visuals */}
                    <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center p-12 text-white bg-[#0F172A] min-h-[600px]">
                        <div className="absolute inset-0">
                            <img
                                src="/assets/images/hero_image.png"
                                alt="Healthcare Professional"
                                className="w-full h-full object-cover opacity-40 mix-blend-overlay"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] opacity-90" />
                        </div>
                        <div className="absolute inset-0 bg-[url('/assets/pattern.svg')] opacity-10 mix-blend-overlay" />

                        <div className="relative z-10 max-w-lg text-center space-y-6">
                            <div className="inline-flex items-center justify-center w-28 h-28 rounded-3xl bg-white shadow-2xl mb-8 border-4 border-white/20 animate-float">
                                <img src="/assets/images/carevault_logo_modern.png" alt="CareVault Logo" className="w-20 h-20 object-contain" />
                            </div>
                            <h2 className="text-4xl font-bold tracking-tight shadow-sm text-white">Secure Your Vault</h2>
                            <p className="text-lg text-blue-100 font-medium leading-relaxed drop-shadow-md">
                                Set a strong new password to ensure your health data remains protected.
                            </p>
                        </div>
                    </div>

                    {/* Right Side - Reset Form */}
                    <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 relative bg-white">
                        <div className="w-full max-w-[420px] space-y-8">
                            {!isSuccess ? (
                                <>
                                    <div className="text-center lg:text-left mb-8">
                                        <h1 className="text-3xl font-bold text-[#1F303B] mb-3">Set New Password</h1>
                                        <p className="text-gray-500 text-base">Please enter and confirm your new password below.</p>
                                    </div>

                                    {isVerifying ? (
                                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
                                            <div className="flex items-center gap-3">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                                <p className="text-blue-700 text-sm font-medium">Verifying your reset link...</p>
                                            </div>
                                        </div>
                                    ) : sessionError ? (
                                        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
                                            <p className="text-red-700 text-sm font-medium">{sessionError}</p>
                                            <Button
                                                onClick={() => navigate('/forgot-password')}
                                                className="mt-3 w-full h-10 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl"
                                            >
                                                Request New Reset Link
                                            </Button>
                                        </div>
                                    ) : null}

                                    <form onSubmit={handleResetPassword} className="space-y-6">
                                        <div className="space-y-5">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">New Password</label>
                                                <div className="relative group">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        className="w-full bg-gray-50 border-2 border-gray-100 focus:bg-white focus:border-[#D64463] rounded-2xl py-4 pl-4 pr-12 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400 shadow-sm"
                                                        placeholder="Enter new password"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    >
                                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">Confirm Password</label>
                                                <div className="relative group">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                        className="w-full bg-gray-50 border-2 border-gray-100 focus:bg-white focus:border-[#D64463] rounded-2xl py-4 pl-4 pr-12 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400 shadow-sm"
                                                        placeholder="Confirm new password"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pt-4">
                                            <Button
                                                type="submit"
                                                className="w-full h-14 bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-[#D64463]/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                                disabled={isLoading || isVerifying || !!sessionError}
                                            >
                                                {isLoading ? "Updating..." : isVerifying ? "Verifying..." : "Reset Password"}
                                            </Button>
                                        </div>
                                    </form>
                                </>
                            ) : (
                                <div className="text-center space-y-6 animate-fade-in-up">
                                    <div className="flex justify-center">
                                        <div className="bg-emerald-50 p-6 rounded-full shadow-lg border border-emerald-100">
                                            <CheckCircle className="h-12 w-12 text-emerald-500" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Password Reset!</h3>
                                        <p className="text-gray-600 text-lg leading-relaxed">
                                            Your password has been successfully updated. You can now log in with your new credentials.
                                        </p>
                                    </div>
                                    <div className="pt-6">
                                        <Button
                                            onClick={() => navigate('/login')}
                                            className="w-full h-14 bg-[#0F172A] hover:bg-[#1E293B] text-white text-lg font-semibold rounded-2xl shadow-lg transition-all"
                                        >
                                            Go to Sign In
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-[#0F172A] w-full mt-auto">
                <UniversalFooter />
            </div>
        </div>
    );
};

export default ResetPasswordPage;
